/**
 * Normalisation de texte pour la recherche (plan v3, phase 6).
 *
 * Un consultant qui tape « perez » doit trouver « Pérez », et « Pérez » doit
 * trouver « perez ». La comparaison se fait donc toujours sur une forme
 * normalisée des deux côtés, jamais sur la chaîne brute.
 *
 * Volontairement plus permissif que `normaliserNom` de `convex/lib/candidateAuth.ts`,
 * qui sert à l'authentification et supprime aussi espaces, tirets et apostrophes :
 * ici les espaces séparent les termes de recherche, ils doivent survivre.
 */

/** Minuscules, sans accents, espaces conservés et compactés. */
export function normaliserTexte(valeur: string | undefined | null): string {
  if (!valeur) return "";
  return valeur
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Vrai si tous les termes de `requete` se trouvent dans l'un des `champs`.
 *
 * Chaque terme est cherché indépendamment : « dupont marie » trouve
 * « Marie Dupont », alors qu'une comparaison de sous-chaîne échouerait.
 */
export function correspondRecherche(
  champs: (string | undefined | null)[],
  requete: string,
): boolean {
  const termes = normaliserTexte(requete).split(" ").filter(Boolean);
  if (termes.length === 0) return true;
  const cible = champs.map(normaliserTexte).filter(Boolean).join(" ");
  return termes.every((terme) => cible.includes(terme));
}

/**
 * Rend une valeur de filtre en texte, sans jamais produire « [object Object] ».
 *
 * Les valeurs de filtre d'une table sont typées `unknown` par TanStack : en
 * pratique une chaîne, un nombre ou une liste, mais rien ne l'impose. Un
 * `String(valeur)` nu stringifierait un objet en « [object Object] » et le
 * poserait tel quel dans l'URL — d'où cette conversion explicite, qui ramène
 * tout ce qui n'est pas une primitive à la chaîne vide (donc « pas de filtre »).
 */
export function enTexte(valeur: unknown): string {
  if (typeof valeur === "string") return valeur;
  if (
    typeof valeur === "number" ||
    typeof valeur === "boolean" ||
    typeof valeur === "bigint"
  ) {
    return String(valeur);
  }
  return "";
}
